/**
 * Cold Storage Service
 *
 * Handles all Cold Storage API calls:
 * - Facility optimization (supports tariff_id or direct energy prices)
 * - Weather data
 */

import { apiPost, handleApiResponse, API_BASE_URL } from '@vortx/shared-ui';

const COLD_STORAGE_PATH = '/api/cold_storage/v1';

/**
 * Optimize a cold storage facility
 * @param {Object} facilityConfig - Facility configuration with zones (matches backend FacilityConfig)
 * @param {Array} ambientTemps - Hourly ambient temps (24 for design day, or N for monthly)
 * @param {Object} options - { tariff_id, start_hour? } or { energy_prices, demand_charge }
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Optimization results
 */
export async function optimizeFacility(facilityConfig, ambientTemps, options, currentUser) {
    // Interpolate hourly → quarter-hour for any length
    const numHours = ambientTemps.length;
    const quarterHourTemps = [];
    for (let i = 0; i < numHours; i++) {
        const curr = ambientTemps[i];
        const next = ambientTemps[(i + 1) % numHours];
        quarterHourTemps.push(
            curr,
            curr + (next - curr) * 0.25,
            curr + (next - curr) * 0.50,
            curr + (next - curr) * 0.75,
        );
    }

    const body = {
        facility_config: facilityConfig,
        ambient_temps: quarterHourTemps,
    };

    if (options.tariff_id) {
        body.tariff_id = options.tariff_id;
    } else if (options.energy_prices) {
        body.energy_prices_usd_kwh = options.energy_prices;
        body.demand_charge_usd_kw = options.demand_charge || 0;
    }

    if (options.start_hour != null) {
        body.start_hour = options.start_hour;
    }

    const response = await apiPost(
        `${API_BASE_URL}${COLD_STORAGE_PATH}/optimize`,
        body,
        currentUser
    );
    return handleApiResponse(response);
}

/**
 * Decompose a facility meter load into cold storage and residual components.
 * @param {Array<number>} meterLoadKw - 8760 hourly meter load values
 * @param {Object} location - { lat, lng }
 * @param {number} loadProfileYear - Year of the load profile
 * @param {Array<Object>} zones - Zone configs from COLD project
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Decomposition results with extracted/residual loads
 */
export async function decomposeMeterLoad(meterLoadKw, location, loadProfileYear, zones, currentUser) {
    const body = {
        meter_load_kw: meterLoadKw,
        location: { lat: location.lat, lng: location.lng },
        load_profile_year: loadProfileYear,
        zones: zones.map(z => ({
            zone_id: z.id || z.zone_id,
            zone_type: z.type || z.zone_type,
            temp_min: z.temp_min,
            temp_max: z.temp_max,
            temp_target: z.target_temp || z.temp_target,
            temp_initial: z.temp_initial || z.target_temp || z.temp_target,
            thermal_resistance_r: z.thermal_resistance_r,
            thermal_capacitance_c: z.thermal_capacitance_c,
            compressor: {
                cooling_capacity_kw: z.compressor.cooling_capacity_kw,
                electrical_power_kw: z.compressor.electrical_power_kw,
                cop: z.compressor.cop,
                min_runtime_hours: z.compressor.min_runtime_hours,
                max_runtime_hours: z.compressor.max_runtime_hours,
                min_off_time_hours: z.compressor.min_off_time_hours,
                min_power_fraction: z.compressor.min_power_fraction || 0.3,
            },
        })),
    };

    const response = await apiPost(
        `${API_BASE_URL}${COLD_STORAGE_PATH}/decompose`,
        body,
        currentUser
    );
    return handleApiResponse(response);
}

export default {
    optimizeFacility,
    decomposeMeterLoad,
};
